/**
 * Toolbar + table-bubble-menu icons. Inline SVGs predate the lucide-react
 * dependency and are kept verbatim; new icons use lucide (see
 * `toolbar-items.ts`). Every glyph is `aria-hidden` — the owning button
 * carries the accessible name, so the SVGs carry NO `<title>`: a title
 * inside an aria-hidden svg is invisible to AT but still surfaces as a
 * plain-English native tooltip when the pointer rests on the glyph.
 */

import { Quote } from 'lucide-react'

// ---------------------------------------------------------------------------
// Inline SVG icons. Kept inline (no lucide-react dep) to avoid a new
// runtime dependency for Phase 2c.
// ---------------------------------------------------------------------------

export function BulletListIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="9" y1="6" x2="20" y2="6" />
      <line x1="9" y1="12" x2="20" y2="12" />
      <line x1="9" y1="18" x2="20" y2="18" />
      <circle cx="4.5" cy="6" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="18" r="1.5" fill="currentColor" />
    </svg>
  )
}

export function OrderedListIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="10" y1="6" x2="20" y2="6" />
      <line x1="10" y1="12" x2="20" y2="12" />
      <line x1="10" y1="18" x2="20" y2="18" />
      <text x="3" y="9" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
        1
      </text>
      <text x="3" y="15" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
        2
      </text>
      <text x="3" y="21" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
        3
      </text>
    </svg>
  )
}

export function TaskListIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="6" height="6" rx="1" />
      <rect x="3" y="14" width="6" height="6" rx="1" />
      <polyline points="4.5,7 5.5,8 7.5,5.5" />
      <line x1="12" y1="7" x2="20" y2="7" />
      <line x1="12" y1="17" x2="20" y2="17" />
    </svg>
  )
}

export function LinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1 1" />
      <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1-1" />
    </svg>
  )
}

export function BlockquoteIcon() {
  /*
   * Quotation marks, not the bar-and-lines glyph. The bar version reads as
   * an indent or a list — it names the RESULT (a bar down the start edge)
   * rather than the action, and no other editor uses it for quoting. This
   * is also the icon `/quote` already shows in the wiki slash menu, so the
   * two surfaces that toggle the same blockquote now look the same.
   */
  return <Quote size={16} aria-hidden="true" />
}

export function CodeBlockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="8,7 3,12 8,17" />
      <polyline points="16,7 21,12 16,17" />
    </svg>
  )
}

export function TableIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="4" x2="9" y2="20" />
      <line x1="15" y1="4" x2="15" y2="20" />
    </svg>
  )
}

// Table bubble-menu icons. Each is a small grid glyph with a directional
// hint (arrow / x) so the affordance reads at 14px. Inline SVG keeps the
// bundle lean — no icon library dep for Phase 2c.

export function RowAboveIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="13" width="18" height="8" rx="1" />
      <line x1="12" y1="3" x2="12" y2="9" />
      <polyline points="9,6 12,3 15,6" />
    </svg>
  )
}

export function RowBelowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="8" rx="1" />
      <line x1="12" y1="15" x2="12" y2="21" />
      <polyline points="9,18 12,21 15,18" />
    </svg>
  )
}

export function RowDeleteIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="9" width="18" height="6" rx="1" />
      <line x1="9" y1="3" x2="15" y2="21" />
      <line x1="15" y1="3" x2="9" y2="21" />
    </svg>
  )
}

export function ColLeftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="13" y="3" width="8" height="18" rx="1" />
      <line x1="3" y1="12" x2="9" y2="12" />
      <polyline points="6,9 3,12 6,15" />
    </svg>
  )
}

export function ColRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="8" height="18" rx="1" />
      <line x1="15" y1="12" x2="21" y2="12" />
      <polyline points="18,9 21,12 18,15" />
    </svg>
  )
}

export function ColDeleteIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="3" width="6" height="18" rx="1" />
      <line x1="3" y1="9" x2="21" y2="15" />
      <line x1="21" y1="9" x2="3" y2="15" />
    </svg>
  )
}

// `TableDeleteIcon` was removed when the delete-table button moved to
// the <TableFooterPopover> (which uses a Lucide `Trash2` glyph). The
// 7-button cell bubble menu no longer carries a table-LEVEL op.

export function AlignLeftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="15" y2="12" />
      <line x1="3" y1="18" x2="18" y2="18" />
    </svg>
  )
}

export function AlignCenterIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="6" y1="12" x2="18" y2="12" />
      <line x1="5" y1="18" x2="19" y2="18" />
    </svg>
  )
}

export function AlignRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="9" y1="12" x2="21" y2="12" />
      <line x1="6" y1="18" x2="21" y2="18" />
    </svg>
  )
}
